<form [formGroup]="form" class="request-form">
  <div class="form-row two-columns dates-row">
    <div class="form-field-wrapper">
      <mat-form-field appearance="outline" floatLabel="always">
        <mat-label>Start Date</mat-label>
        <input
          matInput
          [matDatepicker]="startPicker"
          formControlName="startDate"
          placeholder="mm/dd/yyyy"
        />
        <mat-datepicker-toggle
          matSuffix
          [for]="startPicker"
        ></mat-datepicker-toggle>
        <mat-datepicker #startPicker></mat-datepicker>
      </mat-form-field>
    </div>

    <div class="form-field-wrapper">
      <mat-form-field appearance="outline" floatLabel="always">
        <mat-label>End Date</mat-label>
        <input
          matInput
          [matDatepicker]="endPicker"
          formControlName="endDate"
          [min]="form.get('startDate')?.value"
          placeholder="mm/dd/yyyy"
        />
        <mat-datepicker-toggle
          matSuffix
          [for]="endPicker"
        ></mat-datepicker-toggle>
        <mat-datepicker #endPicker></mat-datepicker>
      </mat-form-field>
      @if (form.hasError("dateRange")) {
        <div class="date-range-error">
          End date must be on or after the start date.
        </div>
      }
    </div>
  </div>

  <div class="form-section client-matter-section">
    <div class="client-matter-container">
      <div class="client-matter-search-wrapper">
        <div class="client-matter-step">
          <div class="client-matter-search-wrapper-inner">
            <!-- Step 1: Search and select a client -->
            @if (!selectedClient) {
              <mat-form-field
                appearance="outline"
                class="search-field"
                floatLabel="always"
              >
                <mat-label>Client</mat-label>
                <mat-icon matPrefix>search</mat-icon>
                <input
                  matInput
                  formControlName="clientSearch"
                  placeholder="Search by name, client #, or client-matter #..."
                  autocomplete="off"
                  (input)="onClientInput($event)"
                  (blur)="onClientBlur()"
                />
                @if (isLoadingClients) {
                  <mat-spinner
                    matSuffix
                    diameter="16"
                    class="field-spinner"
                  ></mat-spinner>
                }
              </mat-form-field>

              @if (clientSuggestions.length > 0) {
                <div class="client-matter-suggestions">
                  @for (c of clientSuggestions; track c.clientNumber) {
                    <div class="suggestion-item" (click)="selectClient(c)">
                      <div class="client-matter-info">
                        <span class="client-matter-name">{{
                          c.clientName
                        }}</span>
                        <span class="client-matter-id"
                          >#{{ c.clientNumber }}</span
                        >
                      </div>
                    </div>
                  }
                </div>
              } @else if (
                clientsNotFound &&
                !isLoadingClients &&
                (form.get("clientSearch")?.value || "").trim().length >= 3
              ) {
                <div class="client-matter-suggestions">
                  <div class="suggestion-item no-results">
                    <div class="client-matter-info">
                      <span class="no-results-text">No clients found</span>
                    </div>
                  </div>
                </div>
              }

              @if (clientMatterError) {
                <div class="client-matter-error">
                  <mat-icon>error_outline</mat-icon>
                  <span>{{ clientMatterError }}</span>
                </div>
              }
            } @else {
              <!-- Step 2: Pick matters for the selected client -->
              <mat-form-field
                appearance="outline"
                class="search-field matter-filter-field"
                floatLabel="always"
              >
                <mat-label>Matters</mat-label>
                <mat-icon matPrefix>search</mat-icon>
                <input
                  matInput
                  formControlName="matterFilter"
                  placeholder="Filter by name, number, or client-matter #..."
                  autocomplete="off"
                />
                @if (isLoadingMatters) {
                  <mat-spinner
                    matSuffix
                    diameter="16"
                    class="field-spinner"
                  ></mat-spinner>
                }
              </mat-form-field>

              @if (matterError) {
                <div class="client-matter-error">
                  <mat-icon>error_outline</mat-icon>
                  <span>{{ matterError }}</span>
                </div>
              }

              <div class="matters-table-container">
                <table mat-table [dataSource]="matters" class="matters-table">
                  <ng-container matColumnDef="matter">
                    <th mat-header-cell *matHeaderCellDef>
                      <div class="matter-header-context">
                        <div class="matter-header-client">
                          <span class="context-text">
                            <strong>{{ selectedClient.clientName }}</strong>
                            <span class="context-number"
                              >#{{ selectedClient.clientNumber }}</span
                            >
                          </span>
                        </div>
                      </div>
                    </th>
                    <td mat-cell *matCellDef="let m">
                      <div class="matter-cell">
                        <span class="matter-cell-name">{{ m.matterName }}</span>
                        <span class="matter-cell-number">{{
                          m.clientMatterNumber
                        }}</span>
                      </div>
                    </td>
                  </ng-container>

                  <ng-container matColumnDef="action">
                    <th mat-header-cell *matHeaderCellDef class="action-header-cell">
                      <button
                        mat-stroked-button
                        type="button"
                        class="context-clear"
                        (click)="clearSelectedClient()"
                      >
                        <mat-icon>swap_horiz</mat-icon>
                        Change client
                      </button>
                    </th>
                    <td mat-cell *matCellDef="let m" class="matter-action-cell">
                      @if (isMatterAdded(m.clientMatterNumber)) {
                        <span class="matter-added">
                          <mat-icon>check</mat-icon>
                          Added
                        </span>
                      } @else {
                        <mat-icon class="add-icon">add_circle</mat-icon>
                      }
                    </td>
                  </ng-container>

                  <tr mat-header-row *matHeaderRowDef="matterColumns"></tr>
                  <tr
                    mat-row
                    *matRowDef="let row; columns: matterColumns"
                    class="matter-row"
                    [class.matter-row--added]="
                      isMatterAdded(row.clientMatterNumber)
                    "
                    (click)="addMatter(row)"
                  ></tr>
                </table>

                @if (matters.length > 0) {
                  <mat-paginator
                    class="matters-paginator"
                    [length]="matterTotalCount"
                    [pageSize]="matterPageSize"
                    [pageIndex]="matterPageIndex"
                    [pageSizeOptions]="matterPageSizeOptions"
                    (page)="onMatterPage($event)"
                  ></mat-paginator>
                } @else if (!isLoadingMatters) {
                  <div class="matters-empty">
                    No matters found for this client.
                  </div>
                }
              </div>
            }
          </div>
        </div>
      </div>

      <div class="client-matter-display-container">
        @if (selectedClientMatters.length > 0) {
          <div class="selected-client-matters-list">
            @for (
              matter of selectedClientMatters;
              track matter.clientMatterNumber
            ) {
              <div class="selected-client-matter">
                <div class="selected-matter-info">
                  <span class="matter-client-name">{{ matter.clientName }}</span>
                  <span class="matter-title">{{ matter.matterName }}</span>
                  <span class="matter-number">{{ matter.clientMatterNumber }}</span>
                </div>
                <button
                  mat-icon-button
                  type="button"
                  class="clear-button"
                  (click)="removeMatter(matter)"
                  title="Remove matter"
                >
                  <mat-icon class="remove-icon">remove_circle</mat-icon>
                </button>
              </div>
            }
          </div>
        } @else {
          <div class="client-matter-placeholder">
            Selected matters will be displayed here...
          </div>
        }
      </div>
    </div>
  </div>
</form>
